import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
    getAllApplications,
    getApplicationJobs,
    getApplications,
    getJobFunctions,
    getLocations,
    updateJob as updateJobRequest,
    updateJobStatus
} from '../api/auto-apply'
import { useNotifications } from './notifications.store';

const STATUS_OPTIONS = {
    offer: "offer",
    notChosen: "notChosen",
    applied: "applied",
    interview: "interview",
    failed: 'failed',
    pending: 'pending',
    expired: 'expired',
}

const countAsAppliedStatus = [
    STATUS_OPTIONS.applied,
    STATUS_OPTIONS.interview,
    STATUS_OPTIONS.notChosen,
    STATUS_OPTIONS.offer
]

const JOBS_PAGE_KEY = 'jobsPage'
const APPLICATIONS_PAGE_KEY = 'applicationsPageKey'

export const useAutoApplyJobsStore = defineStore('auto-apply', () => {
    const jobs = ref([])
    const applications = ref(null)
    const showOnboardingModal = ref(false)
    const loading =  ref(false)
    const loaded =  ref(false)
    const jobFunctionsLoaded = ref(false)
    const locationsLoaded = ref(false)
    const page = ref(parseInt(localStorage.getItem(JOBS_PAGE_KEY)) || 1)
    const totalPages = ref(0)
    const searchStr = ref("")
    const resultLength = ref(0)

    const showJobsBy = ref(20)

    const statusFilter = ref([])
    const locationSelectedOptions = ref([])
    const companyNameSelectedOptions = ref([])
    const coverLetterSelectedOptions = ref([])
    const resumeSelectedOptions = ref([])
    const sortByDateAppliedAscendent = ref(null)
    const sortByRelevancyScoreAscendent = ref(null)
    const jobFunctions = ref(["Other"]);
    const selectedApplication = ref(null);
    const dashboardOnboarding  = ref(false);
    const dashboardLoaded  = ref(true);

    const notificationsStore = useNotifications()

    const pendingJobsCount = computed(() => {
        let count = 0;

        jobs.value.forEach(job => {
            if(job.status === STATUS_OPTIONS.pending ) {
                count++
            }
        })

        return count;
    })

    const locationOptionSelectHandler = (option) => {
        const optionIndex = locationSelectedOptions.value.indexOf(option)

        if(optionIndex !== -1){
            locationSelectedOptions.value.splice(optionIndex, 1)
        }else{
            locationSelectedOptions.value.push(option)
        }
    }

    const resumeOptionSelectHandler = (option) => {
        const optionIndex = resumeSelectedOptions.value.indexOf(option)

        if(optionIndex !== -1){
            resumeSelectedOptions.value.splice(optionIndex, 1)
        }else{
            resumeSelectedOptions.value.push(option)
        }
    }

    const coverLetterOptionSelectHandler = (option) => {
        const optionIndex = coverLetterSelectedOptions.value.indexOf(option)

        if(optionIndex !== -1){
            coverLetterSelectedOptions.value.splice(optionIndex, 1)
        }else{
            coverLetterSelectedOptions.value.push(option)
        }
    }

    const companyNameOptionSelectHandler = (option) => {
        const optionIndex = companyNameSelectedOptions.value.indexOf(option)

        if(optionIndex !== -1){
            companyNameSelectedOptions.value.splice(optionIndex, 1)
        }else{
            companyNameSelectedOptions.value.push(option)
        }
    }

    const showJobs = computed(() => {
        let result = [...jobs?.value]
            .filter(job => {
                const re = new RegExp(searchStr.value, "i")
                const nameMatch = job?.options?.title?.search(re) !== -1

                if(nameMatch === false) return false

                //status filter
                if(statusFilter.value.length > 0) {
                    let passNext = false

                    if(job?.status == null) {
                        if(statusFilter.value.indexOf('notApplied') === -1){
                            return false
                        }else{
                            passNext = true
                        }
                    }

                    const statusMatch = passNext ? [...statusFilter.value, null].includes(job?.status) : statusFilter.value.includes(job?.status)

                    if(statusMatch === false){
                        return false
                    }
                }

                // location filter
                if(locationSelectedOptions.value.length > 0 && locationSelectedOptions.value.indexOf(job?.options?.locations?.join(', ')) === -1) return false
                //company name filter
                if(companyNameSelectedOptions.value.length > 0 && companyNameSelectedOptions.value.indexOf(job?.options?.company) === -1) return false
                // resume filter
                if(resumeSelectedOptions.value.length > 0 && resumeSelectedOptions.value.indexOf(job?.resume?.uuid) === -1) return false
                // cover letter filter
                if(coverLetterSelectedOptions.value.length > 0 && coverLetterSelectedOptions.value.indexOf(job?.cover_letter?.uuid) === -1) return false

                return true
            })

        if(sortByDateAppliedAscendent.value != null) {
            result = result.sort((a, b) => {
                if(sortByDateAppliedAscendent.value){
                    return new Date(a?.applied_at) - new Date(b?.applied_at)
                }else{
                    return new Date(b?.applied_at) - new Date(a?.applied_at)
                }
            })
        }

        if(searchStr.value !== "" && searchStr.value != null){
            page.value = 1
        }

        totalPages.value = Math.ceil(result.length / showJobsBy.value)
        resultLength.value = result.length

        return result.slice((page.value - 1) * showJobsBy.value, page.value * showJobsBy.value)
    })

    const dashboardProgress = computed(() => {
        applications.value;

        const progress = {
            bestResume: {
                resume: null,
                interviewsLanded: 0,
            },
            bestCoverLetter: {
                coverLetter: null,
                interviewsLanded: 0,
            },
            labelsStats: {
                offers: 0,
                interviews: 0,
                applied: 0
            },
            hoursSaved: 0
        }

        const resumes = {}
        const coverLetters = {}
        let totalApplied = 0

        // count statuses
        jobs.value.forEach((job) => {
            const { status, resume, cover_letter } = job;

            if(countAsAppliedStatus.includes(status)){
                totalApplied++
            }

            if (status === STATUS_OPTIONS.offer) {
                progress.labelsStats.offers++;
            } else if (status === STATUS_OPTIONS.interview) {
                progress.labelsStats.interviews++;
                // We still need to check resume since we don't have resume in csv import
                if(resume){
                    resumes[resume.uuid] = resumes?.[resume.uuid] + 1 || 1;
                }

                // We still need to check this cover letter we don't have resume in csv import
                if(cover_letter) {
                    coverLetters[cover_letter.uuid] =
                        coverLetters?.[cover_letter.uuid] + 1 || 1;
                }
            } else if (status === STATUS_OPTIONS.applied) {
                progress.labelsStats.applied++;
            }
        });

        // count best resume
        Object.keys(resumes).forEach(resume => {
            if(resumes[resume] > progress.bestResume.interviewsLanded){
                progress.bestResume.interviewsLanded = resumes[resume]
                progress.bestResume.resume = resume
            }
        })

        //count best CL
        Object.keys(coverLetters).forEach(cl => {
            if(coverLetters[cl] > progress.bestCoverLetter.interviewsLanded){
                progress.bestCoverLetter.interviewsLanded = coverLetters[cl]
                progress.bestCoverLetter.coverLetter = cl
            }
        })

        progress.hoursSaved = Math.floor((totalApplied * 30) / 60)

        return progress
    })

    const toogleStatusFilter = (status) => {
        const isStatusAdded = statusFilter.value.indexOf(status) !== -1;

        if (isStatusAdded) {
            statusFilter.value = statusFilter.value.filter((s) => s !== status);
        } else {
            statusFilter.value.push(status);
        }
    };

    const setShowJobsBy = (count) => {
        showJobsBy.value = count
    }

    const fetchApplications = async (resetFilters = false)  => {
        loading.value = true
        loaded.value = false

        if(resetFilters){
            statusFilter.value = []
            locationSelectedOptions.value = []
            companyNameSelectedOptions.value = []
            coverLetterSelectedOptions.value = []
            resumeSelectedOptions.value = []
            sortByDateAppliedAscendent.value = null
            showJobsBy.value = 20
            pageSelectHandler(1)
        }

        getAllApplications().then((res) => {
            applications.value = res.data
            const data = [];

            res.data.forEach(application => {
                const applicationJobs = application.jobs

                if(applicationJobs.length > 0) {
                    applicationJobs.forEach(job => {
                        data.push({ ...job, applicationName: application.job_title })
                    })
                }
            })

            jobs.value = data
            if (selectedApplication.value) {
                jobs.value = jobs.value.filter(job => {
                    return job?.application === selectedApplication.value.uuid
                })
            }
            resultLength.value = data.length
            totalPages.value = Math.ceil(data.length / showJobsBy.value)
        }).catch(err => {
            notificationsStore.notify('Failed to get jobs!', 'error')
        }).finally(() => {
            loaded.value = true
            loading.value = false
        })
    }

    const pageSelectHandler = (newPage) => {
        localStorage.setItem(JOBS_PAGE_KEY, newPage)
        page.value = newPage
    }

    const setJobStatus = async (jobId, status) =>   {
        loading.value = true

        try {
            const res = await updateJobStatus(jobId, status)

            jobs.value = jobs.value.map(j => {
                if(j.uuid === jobId){
                    return {
                        ...res.data,
                        applicationName: j.applicationName
                    }
                }

                return j
            })
        } catch (error) {
            notificationsStore.notify('Failed to change job status!', 'error')
        }
    }

    const updateJob = async (jobId, data) =>   {
        loading.value = true

        try {
            const res = await updateJobRequest(jobId, data)

            jobs.value = jobs.value.map(j => {
                if(j.uuid === jobId){
                    return {
                        ...res.data,
                        applicationName: j.applicationName
                    }
                }

                return j
            })
        } catch (error) {
            notificationsStore.notify('Failed to update job!', 'error')
        }
    }

    const setShowApplicationModal = () => {
        showOnboardingModal.value = true
    }

    const locationOptions = computed(() => {
        const options = []

        jobs.value.forEach(job => {
            const location = job.options?.locations?.join(', ') || null

            if(location != null){
                if(options.indexOf(location) !== -1) return

                options.push(location)
            }
        })

        return options
    })

    const companyNameOptions = computed(() => {
        const options = []

        jobs.value.forEach(job => {
            const name = job.options?.company

            if(!name || options.indexOf(name) !== -1) return

            options.push(name)
        })

        return options
    })

    const resumeOptions = computed(() => {
        const options = []

        jobs.value.forEach((job) => {
            const isNew = options.findIndex((r) => r.id === job.resume.uuid) === -1;

            if (isNew) {
                if (job.resume) {
                    if (job.resume.title) {
                        options.push({
                            name: job.resume.title
                                .replace("<b>", "")
                                .replace("</b>", ""),
                            id: job.resume.uuid,
                        });
                    }
                }
            }
        });

        return options
    })

    const coverLetterOptions = computed(() => {
        const options = []

        jobs.value.forEach(job => {
            const isNew = options.findIndex(cl => cl.id === job.cover_letter.uuid) === -1

            if (isNew) {
                options.push({
                    name: job.cover_letter?.title
                        ?.replace("<b>", "")
                        ?.replace("</b>", "") || 'unnamed',
                    id: job.cover_letter.uuid,
                });
            }
        })

        return options
    })

    const fetchJobFunctions = async () => {
        if(jobFunctionsLoaded.value) return

        const res = await getJobFunctions();

        for (const data of res) {
            jobFunctions.value.push(data.functions);
        }

        jobFunctionsLoaded.value = true
    }

    const locations = async () => {
        return await getLocations();
    }

    const setSelectedApplication = (application) => {
        selectedApplication.value = application
    }
    
    const filterJobs = (status) => {
        return [...jobs.value]
            .filter(job => {
                if (selectedApplication.value && selectedApplication.value.uuid !== job.application) {
                    return false;
                }
                const re = new RegExp(searchStr.value, "i")
                const nameMatch = job?.options?.title?.search(re) !== -1

                if(nameMatch === false) return false

                //status filter
                if(statusFilter.value.length > 0) {
                    let passNext = false

                    if(job?.status == null) {
                        if(statusFilter.value.indexOf('notApplied') === -1){
                            return false
                        }else{
                            passNext = true
                        }
                    }

                    const statusMatch = passNext ? [...statusFilter.value, null].includes(job?.status) : statusFilter.value.includes(job?.status)

                    if(statusMatch === false){
                        return false
                    }
                }

                // location filter
                if(locationSelectedOptions.value.length > 0 && locationSelectedOptions.value.indexOf(job?.options?.locations?.join(', ')) === -1) return false
                //company name filter
                if(companyNameSelectedOptions.value.length > 0 && companyNameSelectedOptions.value.indexOf(job?.options?.company) === -1) return false
                // resume filter
                if(resumeSelectedOptions.value.length > 0 && resumeSelectedOptions.value.indexOf(job?.resume?.uuid) === -1) return false
                // cover letter filter
                if(coverLetterSelectedOptions.value.length > 0 && coverLetterSelectedOptions.value.indexOf(job?.cover_letter?.uuid) === -1) return false

                if (status) {
                    return job?.status != null
                } else {
                    return job?.status == null
                }
            })
    }

    const availableJobs = computed(() => {
        let result = filterJobs(false)

        if(sortByRelevancyScoreAscendent.value != null) {
            result = result.sort((a, b) => {
                const handleNull = (value, order) => {
                    return value !== null ? value : order === 'asc' ? Infinity : -Infinity;
                };

                if (sortByRelevancyScoreAscendent.value) {
                    const scoreA = handleNull(a.score, 'asc');
                    const scoreB = handleNull(b.score, 'asc');
                    return scoreA - scoreB;
                } else {
                    const scoreA = handleNull(a.score, 'desc');
                    const scoreB = handleNull(b.score, 'desc');
                    return scoreB - scoreA;
                }
            })
        }

        totalPages.value = Math.ceil(result.length / showJobsBy.value)
        resultLength.value = result.length

        return result.slice((page.value - 1) * showJobsBy.value, page.value * showJobsBy.value)
    })

    const appliedJobs = computed(() => {
        let result = filterJobs(true)

        if(sortByDateAppliedAscendent.value != null) {
            result = result.sort((a, b) => {
                if(sortByDateAppliedAscendent.value){
                    return new Date(a?.applied_at) - new Date(b?.applied_at)
                }else{
                    return new Date(b?.applied_at) - new Date(a?.applied_at)
                }
            })
        }

        totalPages.value = Math.ceil(result.length / showJobsBy.value)
        resultLength.value = result.length

        return result.slice((page.value - 1) * showJobsBy.value, page.value * showJobsBy.value)
    })

    const userAppliedJobsAutoComplete = computed(() => {
        return appliedJobs.value.map(job => ({
            id: job?.uuid,
            label: `${job?.options?.company} - ${job?.options?.title}`,
        }))
    })
    
    const setDashboardOnboarding = (value) => {
        dashboardOnboarding.value = value;
    }
    
    const prependNewApplication = (application) => {
        applications.value.unshift(application);
        jobs.value = application.jobs.concat(jobs.value);
    }

    const setDashboardLoaded = (value) => {
        dashboardLoaded.value = value;
    }

    return {
        jobs,
        applications,
        page,
        totalPages,
        loading,
        loaded,
        showJobs,
        availableJobs,
        appliedJobs,
        showOnboardingModal,
        dashboardProgress,
        searchStr,
        resultLength,
        statusFilter,
        pendingJobsCount,
        userAppliedJobsAutoComplete,
        setJobStatus,
        updateJob,
        getApplicationJobs,
        fetchApplications,
        setShowApplicationModal,
        pageSelectHandler,
        toogleStatusFilter,
        setShowJobsBy,

        // options
        locationOptions,
        companyNameOptions,
        resumeOptions,
        coverLetterOptions,
        sortByDateAppliedAscendent,

        locationSelectedOptions,
        locationOptionSelectHandler,

        companyNameSelectedOptions,
        companyNameOptionSelectHandler,

        resumeSelectedOptions,
        resumeOptionSelectHandler,

        coverLetterSelectedOptions,
        coverLetterOptionSelectHandler,
        fetchJobFunctions,
        jobFunctions,
        locations,
        setSelectedApplication,
        selectedApplication,
        jobFunctionsLoaded,
        locationsLoaded,
        setDashboardOnboarding,
        dashboardOnboarding,
        prependNewApplication,
        sortByRelevancyScoreAscendent,
        dashboardLoaded,
        setDashboardLoaded
    }
})
